import api from "@/lib/api";

// --- base64url <-> bytes -------------------------------------------------
export function b64urlToBytes(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export function bytesToB64url(x) {
  const bytes = new Uint8Array(x);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function creationOptions(j) {
  return {
    ...j,
    challenge: b64urlToBytes(j.challenge),
    user: { ...j.user, id: b64urlToBytes(j.user.id) },
    excludeCredentials: (j.excludeCredentials ?? []).map((x) => ({ ...x, id: b64urlToBytes(x.id) })),
  };
}

function requestOptions(j) {
  return {
    ...j,
    challenge: b64urlToBytes(j.challenge),
    allowCredentials: (j.allowCredentials ?? []).map((x) => ({ ...x, id: b64urlToBytes(x.id) })),
  };
}

function registrationJSON(c) {
  const r = c.response;
  return {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment,
    clientExtensionResults: c.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      attestationObject: bytesToB64url(r.attestationObject),
      transports: r.getTransports?.() ?? ["internal"],
    },
  };
}

function authenticationJSON(c) {
  const r = c.response;
  return {
    id: c.id,
    rawId: bytesToB64url(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment,
    clientExtensionResults: c.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64url(r.clientDataJSON),
      authenticatorData: bytesToB64url(r.authenticatorData),
      signature: bytesToB64url(r.signature),
      userHandle: r.userHandle ? bytesToB64url(r.userHandle) : null,
    },
  };
}

// --- capability check ----------------------------------------------------
export function canOfferBiometricUnlock() {
  return (
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    typeof navigator.credentials?.create === "function" &&
    typeof navigator.credentials?.get === "function"
  );
}

// --- enrol a passkey (must already be signed in) --------------------------
export async function registerPasskey() {
  const { data } = await api.post("/passkeys/registration/options", { origin: window.location.origin });
  const credential = await navigator.credentials.create({ publicKey: creationOptions(data.options) });
  if (!credential) throw new Error("Passkey setup cancelled");
  await api.post("/passkeys/registration/verify", {
    challenge_id: data.challenge_id,
    credential: registrationJSON(credential),
  });
  localStorage.setItem("fl_passkey", "1");
}

// --- biometric sign-in from the login screen ------------------------------
export async function unlockWithPasskey() {
  const { data } = await api.post("/passkeys/authentication/options", { origin: window.location.origin });
  const credential = await navigator.credentials.get({ publicKey: requestOptions(data.options) });
  if (!credential) throw new Error("Unlock cancelled");
  const result = await api.post("/passkeys/authentication/verify", {
    challenge_id: data.challenge_id,
    credential: authenticationJSON(credential),
  });
  return result.data; // same shape as password login (user + tokens)
}

export async function listPasskeys() {
  const { data } = await api.get("/passkeys/mine");
  return data;
}

export async function deletePasskey(id) {
  await api.delete(`/passkeys/${id}`);
}
